import React, { useState, useEffect, useRef } from 'react';
import CalendarView from '../components/CalendarView';
import OrganizationFilter from '../components/OrganizationFilter';
import EventListView, { isEmailDigestRequested } from '../components/EventListView';
import LandscapeRail from '../components/LandscapeRail';
import useCompactLandscape from '../hooks/useCompactLandscape';
import EventMapView from '../components/EventMapView';
import config from '../config';
import featureFlags from '../config/featureFlags';
import useDataVersionPolling from '../hooks/useDataVersionPolling';
import { Link } from 'react-router-dom';
import { eventHasOrganization, eventMatchesSlug, getEventGridDayKeys, getEventHosts, getLocalDateKey, isEventStarred, mergeCoHostedEvents, normalizeEventTags, parseEventSlug } from '../utils/eventUtils';
import { CATEGORIES, CATEGORY_MATCH_ORDER, categorizeEvent } from '../config/categories';
import { useSwipeable } from 'react-swipeable';
import { useTheme } from '../context/ThemeContext';
import TextScaleToggle from '../components/TextScaleToggle';
import { fs, fsClamp } from '../utils/typography';

function Home() {
    const { theme, themeName, toggleTheme } = useTheme();

    // Get current month and year
    const now = new Date();   
    const thisMonth = now.getMonth() + 1;
    const thisYear = now.getFullYear();

    // Set initial view mode — persisted for returning users, defaults to week if returning, month for first visit
    const getInitialViewMode = () => {
        // ?view=email (or legacy #email) opens the List view in email digest mode
        if (isEmailDigestRequested() && featureFlags.list.enabled) return 'list';
        const saved = localStorage.getItem('lastViewMode');
        if (saved) {
            if (saved === 'month' && featureFlags.calendar.enabled) return 'month';
            if (saved === 'week' && featureFlags.calendar.enabled) return 'week';
            if (saved === 'list' && featureFlags.list.enabled) return 'list';
            if (saved === 'map' && featureFlags.map.enabled) return 'map';
        }
        // No saved view: returning users (have any other stored prefs) default to week
        const isReturningUser = localStorage.getItem('disclaimer-dismissed') !== null
            || localStorage.getItem('starredEvents') !== null
            || localStorage.getItem('hidePrivateEvents') !== null;
        if (isReturningUser && featureFlags.calendar.enabled) return 'week';
        // First-time visitors
        if (featureFlags.calendar.enabled) return 'month';
        if (featureFlags.list.enabled) return 'list';
        if (featureFlags.map.enabled) return 'map';
        return 'month';
    };

    // State management
    const [curMonth, setValue] = useState(thisMonth);
    const [curYear, setYear] = useState(thisYear);
    const [isLoading, setIsLoading] = useState(false);
    //const [isAnimating, setIsAnimating] = useState(false);
    //const [swipeDirection, setSwipeDirection] = useState(null);
    const [viewMode, setViewMode] = useState(getInitialViewMode());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showMonthPicker, setShowMonthPicker] = useState(false);
    const [events, setEvents] = useState([]);
    const [combinedEvents, setCombinedEvents] = useState([]);
    const [selectedEventSlug, setSelectedEventSlug] = useState(null);
    const [pendingHash, setPendingHash] = useState(null);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [refreshTick, setRefreshTick] = useState(0);
    //const [shouldOpenModal, setShouldOpenModal] = useState(false);
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedTags, setSelectedTags] = useState(() => new Set());
    const [toast, setToast] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [showWeekPicker, setShowWeekPicker] = useState(false);
    const [currentWeek, setCurrentWeek] = useState(() => {
        const today = new Date();
        const currentDay = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);
        return startOfWeek;
    });
    const toastTimeout = useRef(null);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showLegal, setShowLegal] = useState(false);
    const isCompactLandscape = useCompactLandscape();
    const [isRightPaneOpen, setIsRightPaneOpen] = useState(false);
    const processedSlugRef = useRef(null);
    const [showStarredOnly, setShowStarredOnly] = useState(false);
    const [hidePrivateEvents, setHidePrivateEvents] = useState(
        () => localStorage.getItem('hidePrivateEvents') === 'true'
    );
    const [disclaimerDismissed, setDisclaimerDismissed] = useState(
        () => localStorage.getItem('disclaimer-dismissed') === 'true'
    );
    const [showDisclaimer, setShowDisclaimer] = useState(false);
    const disclaimerRef = useRef(null);

    // Helper function to fetch events for a specific month
    const fetchEventsForMonth = async (year, month) => {
        const cacheKey = `events-${year}-${month}`;
        const versionKey = 'events-api-version';
        const cachedData = localStorage.getItem(cacheKey);
        const cachedVersion = localStorage.getItem(versionKey);
        
        try {
            // Check API version first
            const versionResponse = await fetch(`${config.apiBaseUrl}${config.endpoints.events.version}`);
            const currentVersion = await versionResponse.text();

            if (cachedVersion !== currentVersion) {
                Object.keys(localStorage).forEach(key => {
                    if (key.startsWith('events-')) {
                        localStorage.removeItem(key);
                    }
                });
                localStorage.setItem(versionKey, currentVersion);
            } else if (cachedData) {
                return JSON.parse(cachedData);
            }

            const response = await fetch(`${config.apiBaseUrl}${config.endpoints.events.monthly}/${year}/${month}`);
            const data = await response.json();
            localStorage.setItem(cacheKey, JSON.stringify(data));
            return data;
        } catch (error) {
            //console.error(`Error fetching events for ${month}/${year}:`, error);
            if (cachedData) {
                return JSON.parse(cachedData);
            }
            return [];
        }
    };

    // Fetch events for the current month
    useEffect(() => {
        const fetchEvents = async () => {
            setIsLoading(true);
            
            try {
                const data = await fetchEventsForMonth(curYear, curMonth);
                setEvents(mergeCoHostedEvents(data, { categorize: categorizeEvent, categoryPriority: CATEGORY_MATCH_ORDER }));
            } catch (error) {
                //console.error('Error fetching events:', error);
                setEvents([]);
            } finally {
                setIsLoading(false);
                setInitialLoadComplete(true);
            }
        }

        fetchEvents();
    }, [curMonth, curYear, refreshTick]);

    // Fetch combined events for cross-month weeks
    useEffect(() => {
        const fetchCombinedEvents = async () => {
            if (viewMode === 'week' && currentWeek) {
                const endOfWeek = new Date(currentWeek);
                endOfWeek.setDate(currentWeek.getDate() + 6);
                
                const startMonth = currentWeek.getMonth() + 1;
                const startYear = currentWeek.getFullYear();
                const endMonth = endOfWeek.getMonth() + 1;
                const endYear = endOfWeek.getFullYear();
                
                // Check if week spans multiple months
                if (startMonth !== endMonth || startYear !== endYear) {
                    setIsLoading(true);
                    try {
                        // Fetch events for both months
                        const [startMonthEvents, endMonthEvents] = await Promise.all([
                            fetchEventsForMonth(startYear, startMonth),
                            fetchEventsForMonth(endYear, endMonth)
                        ]);
                        
                        // Combine and filter events for the specific week
                        const weekStart = getLocalDateKey(currentWeek);
                        const weekEnd = getLocalDateKey(endOfWeek);
                        
                        const combined = [...startMonthEvents, ...endMonthEvents].filter(event =>
                            getEventGridDayKeys(event).some(key => key >= weekStart && key <= weekEnd)
                        );
                        
                        setCombinedEvents(mergeCoHostedEvents(combined, { categorize: categorizeEvent, categoryPriority: CATEGORY_MATCH_ORDER }));
                    } catch (error) {
                        //console.error('Error fetching combined events:', error);
                        setCombinedEvents([]);
                    } finally {
                        setIsLoading(false);
                    }
                } else {
                    // Week is within single month, clear combined events
                    setCombinedEvents([]);
                }
            } else {
                // Not in week view, clear combined events
                setCombinedEvents([]);
            }
        };

        fetchCombinedEvents();
    }, [viewMode, currentWeek, curMonth, curYear, refreshTick]);

    // Poll the events dataversion endpoint; when it changes, bump refreshTick so the
    // fetch effects above re-run. fetchEventsForMonth compares against the cached
    // version itself and busts the localStorage cache, so this just triggers the refetch.
    useDataVersionPolling(() => {
        setRefreshTick(tick => tick + 1);
        showToast('Calendar updated with new events');
    });

    // Extract unique organizations whenever events change. A co-hosted event
    // contributes each of its host organizations, so it still shows up when
    // filtering by any one of them.
    useEffect(() => {
        if (events && events.length > 0) {
            const orgSet = new Set();
            events.forEach(event => {
                getEventHosts(event).forEach(host => {
                    if (host.organization) orgSet.add(host.organization);
                });
            });
            setOrganizations([...orgSet].sort());
        } else {
            setOrganizations([]);
        }
    }, [events]);

    // Filter events based on selected organization, view mode, and starred filter.
    // This is the event set the category/tag legend derives its counts from —
    // i.e. everything *except* the category/tag filters themselves, so a
    // category's count reflects "if picked, how many events would show".
    const legendEvents = (() => {
        let eventsToFilter = events;

        // Use combined events for week view if available
        if (viewMode === 'week' && combinedEvents.length > 0) {
            eventsToFilter = combinedEvents;
        }

        // Starred filter
        if (showStarredOnly) {
            const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
            eventsToFilter = eventsToFilter.filter(event => isEventStarred(event, starred));
        }

        // Hide private events filter
        if (hidePrivateEvents) {
            eventsToFilter = eventsToFilter.filter(event => event.eventURL !== '/private-events');
        }

        return selectedOrg === 'all'
            ? eventsToFilter
            : eventsToFilter.filter(event => eventHasOrganization(event, selectedOrg));
    })();

    // Category (single-select) + tags (multi-select, AND'd) layer on top of
    // the org/starred/private filters above.
    const filteredEvents = (() => {
        let eventsToFilter = selectedCategory
            ? legendEvents.filter(event => categorizeEvent(event) === selectedCategory)
            : legendEvents;

        if (selectedTags.size > 0) {
            eventsToFilter = eventsToFilter.filter(event => {
                const eventTagKeys = normalizeEventTags(event.tags).map(t => t.toLowerCase());
                return [...selectedTags].every(tagKey => eventTagKeys.includes(tagKey));
            });
        }

        return eventsToFilter;
    })();

    // Events actually visible in the current week grid (mirrors CalendarView's
    // getCurrentWeekEvents). filteredEvents can still hold a whole month's worth
    // of events in week view when the week doesn't cross a month boundary (see
    // fetchCombinedEvents above), so the header count needs its own week filter
    // to match what's on screen.
    const currentWeekEvents = (() => {
        if (viewMode !== 'week' || !currentWeek) return filteredEvents;
        const weekDates = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(currentWeek);
            d.setDate(currentWeek.getDate() + i);
            weekDates.push(getLocalDateKey(d));
        }
        return filteredEvents.filter(event => getEventGridDayKeys(event).some(key => weekDates.includes(key)));
    })();


        const isSwipeFromDayPane = (eventData) => {
                const target = eventData?.event?.target;
                return !!(target && target.closest && target.closest('[data-day-swipe-zone="true"]'));
        };

        const handlers = useSwipeable({
                onSwipedLeft: (eventData) => {
                    if (viewMode === 'week') return;
                    if (isSwipeFromDayPane(eventData)) return;
                    if (!isModalOpen && !isRightPaneOpen && window.innerWidth < 768) {
                        handleIncrement();
                    }
                },
                onSwipedRight: (eventData) => {
                    if (viewMode === 'week') return;
                    if (isSwipeFromDayPane(eventData)) return;
                    if (!isModalOpen && !isRightPaneOpen && window.innerWidth < 768) {
                        handleDecrement();
                    }
                },
        preventDefaultTouchmoveEvent: true,
        trackMouse: false
      });

    const handleHidePrivateChange = (val) => {
        localStorage.setItem('hidePrivateEvents', String(val));
        setHidePrivateEvents(val);
    };

    // Category is single-select: clicking the active category clears it.
    // Tags are scoped to the selected category, so switching categories
    // drops any tag selections that may no longer apply.
    const handleCategoryClick = (cat) => {
        setSelectedCategory(prev => (prev === cat ? null : cat));
        setSelectedTags(new Set());
    };

    // Tags are multi-select (AND'd together).
    const handleTagClick = (tagKey) => {
        setSelectedTags(prev => {
            const next = new Set(prev);
            if (next.has(tagKey)) next.delete(tagKey);
            else next.add(tagKey);
            return next;
        });
    };

    const dismissDisclaimer = () => {
        localStorage.setItem('disclaimer-dismissed', 'true');
        setDisclaimerDismissed(true);
        setShowDisclaimer(false);
    };

    // Close disclaimer popover when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (disclaimerRef.current && !disclaimerRef.current.contains(e.target)) {
                setShowDisclaimer(false);
            }
        };
        if (showDisclaimer) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showDisclaimer]);

    // Show toast for 3 seconds
    const showToast = (message) => {
        setToast(message);
        if (toastTimeout.current) clearTimeout(toastTimeout.current);
        toastTimeout.current = setTimeout(() => setToast(null), 3000);
    };

    const clearSelectedEventSlug = () => {
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        window.history.pushState({}, '', window.location.pathname);
        //console.log('Cleared selected event slug');
    };

    // Expose showToast globally for child components
    useEffect(() => {
        window.showToast = showToast;

        return () => {
            delete window.showToast;

        };
    }, []);

    const gotoToday = () => {
        setValue(thisMonth);
        setYear(thisYear);
        setShowMonthPicker(false);
        
        // Reset to current week
        const today = new Date();
        const currentDay = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - currentDay);
        setCurrentWeek(startOfWeek);
        setShowWeekPicker(false);
        
        // clear the # in the url
        window.history.pushState({}, '', window.location.pathname);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        
        // Close right pane on small screens
        if (window.innerWidth < 768) {
          setIsRightPaneOpen(false);
        }
    }
    
   
      const handleIncrement = () => {
        if (viewMode === 'week') {
          // Week navigation
          const newWeek = new Date(currentWeek);
          newWeek.setDate(newWeek.getDate() + 7);
          setCurrentWeek(newWeek);
          setShowWeekPicker(false);
          
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
          
          // Check if the new week spans a different month and update month/year if needed
          const endOfNewWeek = new Date(newWeek);
          endOfNewWeek.setDate(newWeek.getDate() + 6);
          const newWeekMonth = newWeek.getMonth() + 1;
          const newWeekYear = newWeek.getFullYear();
          const endWeekMonth = endOfNewWeek.getMonth() + 1;
          const endWeekYear = endOfNewWeek.getFullYear();
          
          // If the week spans multiple months, use the month that has more days in the week
          const daysInStartMonth = 7 - newWeek.getDay();
          const daysInEndMonth = endOfNewWeek.getDay() + 1;
          
          if (daysInStartMonth >= daysInEndMonth) {
            // More days in start month, or equal
            if (newWeekMonth !== curMonth || newWeekYear !== curYear) {
              setValue(newWeekMonth);
              setYear(newWeekYear);
            }
          } else {
            // More days in end month
            if (endWeekMonth !== curMonth || endWeekYear !== curYear) {
              setValue(endWeekMonth);
              setYear(endWeekYear);
            }
          }
        } else {
          // Month navigation
          setValue(prevValue => prevValue + 1);
          setShowMonthPicker(false);
          if (curMonth === 12) {
            setValue(1);
            setYear(prevValue => prevValue + 1);
          }
          
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
        }
      }
    
      const handleDecrement = () => {
        if (viewMode === 'week') {
          // Week navigation
          const newWeek = new Date(currentWeek);
          newWeek.setDate(newWeek.getDate() - 7);
          setCurrentWeek(newWeek);
          setShowWeekPicker(false);
 
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
          
          // Check if the new week spans a different month and update month/year if needed
          const endOfNewWeek = new Date(newWeek);
          endOfNewWeek.setDate(newWeek.getDate() + 6);
          const newWeekMonth = newWeek.getMonth() + 1;
          const newWeekYear = newWeek.getFullYear();
          const endWeekMonth = endOfNewWeek.getMonth() + 1;
          const endWeekYear = endOfNewWeek.getFullYear();
          
          // If the week spans multiple months, use the month that has more days in the week
          const daysInStartMonth = 7 - newWeek.getDay();
          const daysInEndMonth = endOfNewWeek.getDay() + 1;
          
          if (daysInStartMonth >= daysInEndMonth) {
            // More days in start month, or equal
            if (newWeekMonth !== curMonth || newWeekYear !== curYear) {
              setValue(newWeekMonth);
              setYear(newWeekYear);
            }
          } else {
            // More days in end month
            if (endWeekMonth !== curMonth || endWeekYear !== curYear) {
              setValue(endWeekMonth);
              setYear(endWeekYear);
            }
          }
        } else {
          // Month navigation
          setValue(prevValue => prevValue - 1);
          setShowMonthPicker(false);
          if (curMonth === 1) {
            setValue(12);
            setYear(prevValue => prevValue - 1);
          }
          
          // clear the # in the url
          window.history.pushState({}, '', window.location.pathname);
          setSelectedEventSlug(null);
          processedSlugRef.current = null;
          
          // Close right pane on small screens
          if (window.innerWidth < 768) {
            setIsRightPaneOpen(false);
          }
        }
      }

    // Watch for month/org changes and auto-switch if no events for org
    useEffect(() => {
        if (selectedOrg !== 'all' && !isLoading && initialLoadComplete) {
            const orgEvents = events.filter(event => eventHasOrganization(event, selectedOrg));
            if (orgEvents.length === 0) {
                showToast('No events this month for the selected organization, switching to All Organizations.');
                setSelectedOrg('all');
            }
        }
    }, [curMonth, curYear, selectedOrg, events, isLoading, initialLoadComplete]);

    // Handle slug navigation when events are loaded
    useEffect(() => {
        if (selectedEventSlug && initialLoadComplete) {
            // Prevent re-processing the same slug
            if (processedSlugRef.current === selectedEventSlug) {
                //console.log('Slug already processed, skipping:', selectedEventSlug);
                return;
            }
            
            //console.log('Processing slug navigation:', selectedEventSlug, 'Events loaded:', events.length);
            // Check both regular events and combined events
            const allEvents = viewMode === 'week' && combinedEvents.length > 0 
                ? [...events, ...combinedEvents] 
                : events;
            
            //console.log('Available events:', allEvents.length);
            
            if (allEvents.length > 0) {
                // Find the event by slug
                const targetEvent = allEvents.find(event => {
                    return eventMatchesSlug(event, selectedEventSlug);
                });

                if (targetEvent) {
                    //console.log('Found target event:', targetEvent.title);
                    // Mark this slug as processed
                    processedSlugRef.current = selectedEventSlug;
                    
                    // Navigate to the correct month/year if needed
                    const eventDate = new Date(targetEvent.startDate);
                    const eventMonth = eventDate.getMonth() + 1;
                    const eventYear = eventDate.getFullYear();

                    // Only navigate if we're not already on the correct month/year
                    if (eventMonth !== curMonth || eventYear !== curYear) {
                        //console.log(`Event is in different month: ${eventMonth}/${eventYear}, navigating...`);
                        setValue(eventMonth);
                        setYear(eventYear);
                        // The event will be selected after the month/year change triggers a new fetch
                        // Don't clear the slug yet - let it be processed again after navigation
                    } else {
                        //console.log('Event is in current month, selecting it');
                        // We're already on the correct month, select the event
                        setSelectedEvent(targetEvent);
                        // Don't clear the slug - let it persist for the selected event
                    }
                } else {
                    //console.log('Event not found in current events');
                    // Event not found in current month
                    // Check if we need to navigate to a different month based on the slug
                    const dateInfo = parseEventSlug(selectedEventSlug);
                    if (dateInfo && (dateInfo.month !== curMonth || dateInfo.year !== curYear)) {
                        //console.log(`Navigating to month from slug: ${dateInfo.month}/${dateInfo.year}`);
                        // Navigate to the month/year from the slug
                        setValue(dateInfo.month);
                        setYear(dateInfo.year);
                        // Keep the slug to be processed after the new events load
                    } else {
                        //console.log('Event not found and already on correct month, clearing slug');
                        // Event not found and we're already on the correct month, clear the slug
                        setSelectedEventSlug(null);
                        processedSlugRef.current = null;
                    }
                }
            } else {
                //console.log('No events loaded yet, waiting...');
                // Retry after a delay if events aren't loaded yet
                const retryTimer = setTimeout(() => {
                    if (selectedEventSlug && events.length === 0) {
                        //console.log('Retrying hash processing after delay...');
                        // Force a re-render to retry the processing
                        setSelectedEventSlug(selectedEventSlug);
                    }
                }, 2000);
                
                return () => clearTimeout(retryTimer);
            }
        }
    }, [selectedEventSlug, events, combinedEvents, viewMode, initialLoadComplete, curMonth, curYear]);

    // Process pending hash when events become available
    useEffect(() => {
        if (pendingHash && initialLoadComplete && events.length > 0) {
            //console.log('Processing pending hash:', pendingHash);
            setSelectedEventSlug(pendingHash);
            setPendingHash(null);
        }
    }, [pendingHash, initialLoadComplete, events.length]);

    // Process initial hash after app is fully loaded (backup mechanism)
    useEffect(() => {
        if (initialLoadComplete && !selectedEventSlug) {
            const hash = window.location.hash.slice(1);
            if (hash && hash !== 'email') {
                //console.log('Processing initial hash after app load:', hash);
                setSelectedEventSlug(hash);
            }
        }
    }, [initialLoadComplete, selectedEventSlug]);

    // Handle hash changes
    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.slice(1);
            //console.log('Hash changed:', hash, 'Initial load complete:', initialLoadComplete, 'Events loaded:', events.length);
            // #email toggles the List view's digest mode — it isn't an event slug
            if (hash === 'email') return;
            if (hash) {
                // Parse the slug to extract year and month
                const dateInfo = parseEventSlug(hash);
                //console.log('Parsed date info:', dateInfo);
                if (dateInfo) {
                    // Navigate to the correct month/year first
                    if (dateInfo.month !== curMonth || dateInfo.year !== curYear) {
                        //console.log(`Navigating to ${dateInfo.month}/${dateInfo.year} from ${curMonth}/${curYear}`);
                        setValue(dateInfo.month);
                        setYear(dateInfo.year);
                        // The slug will be processed after the month/year change triggers a new fetch
                    }
                }

                // Only Month/Week (via CalendarView) know how to open an
                // event's detail pane for a slug — List and Map have no
                // inline surface for it, so a slug link clicked from either
                // of those views silently did nothing. Switch to Month view
                // so the linked event actually gets shown.
                if (viewMode !== 'month' && viewMode !== 'week') {
                    setViewMode('month');
                    localStorage.setItem('lastViewMode', 'month');
                }

                // If events are loaded, process immediately; otherwise, store as pending
                if (initialLoadComplete && events.length > 0) {
                    setSelectedEventSlug(hash);
                } else {
                    //console.log('Events not loaded yet, storing hash as pending');
                    setPendingHash(hash);
                }
            } else {
                setSelectedEventSlug(null);
                setPendingHash(null);
            }
        };

        // Process initial hash immediately
        handleHashChange();
        
        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [curMonth, curYear, initialLoadComplete, events.length, viewMode]);

        const setCurMonth = (month, options = {}) => {
                const { preserveRightPane = false } = options;
        setValue(month);
        // clear the # in the url
        window.history.pushState({}, '', window.location.pathname);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        
        // Close right pane on small screens
                if (window.innerWidth < 768 && !preserveRightPane) {
          setIsRightPaneOpen(false);
        }
      }

        const setCurYear = (year, options = {}) => {
                const { preserveRightPane = false } = options;
        setYear(year);
        // clear the # in the url
        window.history.pushState({}, '', window.location.pathname);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
        
        // Close right pane on small screens
                if (window.innerWidth < 768 && !preserveRightPane) {
          setIsRightPaneOpen(false);
        }
    }

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedEventSlug(null);
        processedSlugRef.current = null;
    };

    // View mode setters
    const setMonthView = () => {
        if (featureFlags.calendar.enabled) {
            setViewMode('month');
            localStorage.setItem('lastViewMode', 'month');
            setShowWeekPicker(false);
            setShowMonthPicker(false);
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };
    
    const setWeekView = () => {
        if (featureFlags.calendar.enabled) {
            setViewMode('week');
            localStorage.setItem('lastViewMode', 'week');
            // Set to the current week (today)
            const today = new Date();
            const startOfCurrentWeek = new Date(today);
            startOfCurrentWeek.setDate(today.getDate() - today.getDay());
            setCurrentWeek(startOfCurrentWeek);
            setShowMonthPicker(false);
            setShowWeekPicker(false);
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };

    const setListView = () => {
        if (featureFlags.list.enabled) {
            setViewMode('list');
            localStorage.setItem('lastViewMode', 'list');
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };
    
    const setMapView = () => {
        if (featureFlags.map.enabled) {
            setViewMode('map');
            localStorage.setItem('lastViewMode', 'map');
            // clear the # in the url
            window.history.pushState({}, '', window.location.pathname);
            setSelectedEventSlug(null);
            processedSlugRef.current = null;
            
            // Close right pane on small screens
            if (window.innerWidth < 768) {
              setIsRightPaneOpen(false);
            }
        }
    };

    const renderMonthName = () => {
        const date = new Date(curYear, curMonth - 1, 1);
        return date.toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const renderWeekRange = () => {
        const endOfWeek = new Date(currentWeek);
        endOfWeek.setDate(currentWeek.getDate() + 6);
        
        const startMonth = currentWeek.toLocaleString('default', { month: 'short' });
        const endMonth = endOfWeek.toLocaleString('default', { month: 'short' });
        const startDay = currentWeek.getDate();
        const endDay = endOfWeek.getDate();
        const year = currentWeek.getFullYear();
        
        if (startMonth === endMonth) {
            return `${startMonth} ${startDay}-${endDay}, ${year}`;
        } else {
            return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
        }
    };

    // ISO-ish week number for the WEEK nn · N EVENTS meta string
    const getWeekNumber = (date) => {
        const start = new Date(date.getFullYear(), 0, 1);
        return Math.ceil(((date - start) / 86400000 + start.getDay() + 1) / 7);
    };

    const getWeeksInMonth = (year, month) => {
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        const startOfFirstWeek = new Date(firstDay);
        startOfFirstWeek.setDate(firstDay.getDate() - firstDay.getDay());
        
        const weeks = [];
        let currentWeek = new Date(startOfFirstWeek);
        
        while (currentWeek <= lastDay || weeks.length < 6) {
            weeks.push(new Date(currentWeek));
            currentWeek.setDate(currentWeek.getDate() + 7);
        }
        
        return weeks;
    };

    const tokens = theme.tokens;
    const tc = tokens.colors;

    // Segmented view-switcher item — accent-filled pill when active (Atlanta Editorial)
    const switcherButton = ({ key, onClick, active, icon, label, ariaLabel, title, id, highlight }) => (
        <button
            key={key}
            id={id}
            type="button"
            onClick={onClick}
            className={`flex items-center gap-1.5 rounded-lg px-2 sm:px-3 py-1.5 transition ${
                active ? theme.viewButtonActive : theme.viewButtonInactive
            }`}
            style={highlight && !active ? { color: tc.accent, boxShadow: `inset 0 0 0 1px ${tc.accent}` } : undefined}
            aria-label={ariaLabel}
            title={title || ariaLabel}
            aria-pressed={active}
        >
            <i className={`fas ${icon} text-xs`} aria-hidden="true"></i>
            <span className="hidden sm:inline leading-none font-medium" style={{ fontSize: fs(13) }}>{label}</span>
        </button>
    );

    // View + filter buttons, shared by the portrait/desktop view switcher and
    // the landscape-phone nav rail (LandscapeRail).
    const viewButtons = [
        featureFlags.calendar.enabled && {
            key: 'month', onClick: setMonthView, active: viewMode === 'month',
            icon: 'fa-calendar-alt', label: 'Month', ariaLabel: 'Month view',
        },
        featureFlags.calendar.enabled && {
            key: 'week', onClick: setWeekView, active: viewMode === 'week',
            icon: 'fa-calendar-week', label: 'Week', ariaLabel: 'Week view',
        },
        featureFlags.list.enabled && {
            key: 'list', onClick: setListView, active: viewMode === 'list',
            icon: 'fa-list', label: 'List', ariaLabel: 'List view',
        },
        featureFlags.map.enabled && {
            key: 'map', onClick: setMapView, active: viewMode === 'map',
            icon: 'fa-map-marker-alt', label: 'Map', ariaLabel: 'Map view',
        },
    ].filter(Boolean);
    const filterButton = {
        key: 'filter', id: 'filter-button', onClick: () => setShowFilters(!showFilters),
        active: showFilters,
        highlight: selectedOrg !== 'all' || hidePrivateEvents || !!selectedCategory || selectedTags.size > 0,
        icon: 'fa-filter', label: 'Filter',
        ariaLabel: 'Toggle organization filter panel',
        title: selectedOrg !== 'all' ? `Filtered by ${selectedOrg}` : (selectedCategory ? `Filtered by ${CATEGORIES[selectedCategory].label}` : 'No filters applied'),
    };

    return (
        // Not app-shell-locked here — this is nested inside App.js's <main>,
        // a flex-grow sibling of the mobile header bar within the *already*
        // correctly-sized .App shell. Re-claiming the full viewport height
        // here (rather than just filling the space main actually has left)
        // overflowed .App by exactly the header's height, which iOS Safari
        // and Chrome would both silently "fix" by scrolling <body> — even
        // with page scroll locked — pushing the header up out of view with
        // no way to scroll back to it.
        <div className={`h-full ${theme.text}`} style={{ background: tc.bg }}>
            <h1 className="sr-only">ATLTech.events — Atlanta Tech Meetups &amp; Events Calendar</h1>
            {/* A phone held sideways swaps the top masthead for a left nav rail —
                see useCompactLandscape / LandscapeRail. */}
            <div className={isCompactLandscape ? 'h-full flex' : 'h-full flex flex-col'}>
                {isCompactLandscape && (
                    <LandscapeRail views={viewButtons} filter={filterButton} onShowLegal={() => setShowLegal(true)} />
                )}
                <div className={`h-full flex flex-col ${isCompactLandscape ? 'flex-1 min-w-0' : ''}`} style={{ background: tc.surface }}>
                    {/* Masthead: brand · view switcher · links/theme/Mirability */}
                    <div className="flex-none" style={{ fontFamily: tokens.fonts.ui }}>
                        {!isCompactLandscape && (
                        <div className="flex flex-wrap items-center gap-x-3 lg:gap-x-5 gap-y-1.5 px-3 lg:px-6 py-2" style={{ borderBottom: `1px solid ${tc.gridLine}` }}>
                            {/* Brand (hidden on mobile — MobileHeader covers it) */}
                            <div className="hidden md:flex items-center gap-2.5 flex-none">
                                <Link to="/" className="flex items-center gap-2.5">
                                    <img src="favicon-32x32.png" alt="" className="h-8 w-8 object-contain" />
                                    <span style={{ fontFamily: tokens.fonts.display, fontSize: 19, fontWeight: 600, color: tc.ink, lineHeight: 1 }}>
                                        ATLTech<span style={{ fontFamily: tokens.fonts.mono, fontSize: 16, fontWeight: 500, color: tc.accent }}>.events</span>
                                    </span>
                                </Link>
                                <span style={{
                                    fontFamily: tokens.fonts.mono, fontSize: 9.5, letterSpacing: 0.6, fontWeight: 500,
                                    color: tc.accent, border: `1px solid ${tc.accent}`, borderRadius: 4, padding: '2px 5px', opacity: 0.9,
                                }}>BETA</span>
                            </div>

                            {/* Centered segmented view switcher — wraps below the brand row when space is tight */}
                            <div className="flex-1 flex justify-center">
                                <div className="flex gap-0.5 rounded-[11px] p-[3px]" style={{ background: tc.bg, border: `1px solid ${tc.gridLine}` }}>
                                    {viewButtons.map(switcherButton)}
                                    <div className="relative" ref={disclaimerRef}>
                                        {switcherButton({
                                            key: 'info', onClick: () => setShowDisclaimer(!showDisclaimer), active: false,
                                            icon: 'fa-info-circle', label: 'Info', ariaLabel: 'Event disclaimer',
                                        })}
                                        {showDisclaimer && (
                                            <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-64 ${theme.modalBg} border ${theme.border} rounded-lg shadow-lg p-3 z-50 text-sm ${theme.text}`}>
                                                Note: Events are subject to change. Please verify with the event host.
                                            </div>
                                        )}
                                    </div>
                                    {switcherButton(filterButton)}
                                </div>
                            </div>

                            {/* Right: links · theme toggle · Mirability */}
                            <div className="hidden md:flex flex-col items-end gap-1 flex-none ml-auto">
                                <div className="flex items-center gap-4 font-medium" style={{ fontSize: fs(13) }}>
                                    <a href="/about" className={theme.navLink}>About</a>
                                    <a href="/community" className={theme.navLink}>Community</a>
                                    <a href="/subscribe" className={theme.navLink}>Subscribe</a>
                                    <button type="button" className={theme.navLink} onClick={() => setShowLegal(!showLegal)}>Legal</button>
                                    <TextScaleToggle />
                                    <button
                                        onClick={toggleTheme}
                                        className="flex items-center justify-center"
                                        style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${tc.gridLine}`, color: tc.muted }}
                                        aria-label={themeName === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                                        title={themeName === 'dark' ? 'Light mode' : 'Dark mode'}
                                    >
                                        <i className={`fas fa-${themeName === 'dark' ? 'sun' : 'moon'}`} aria-hidden="true"></i>
                                    </button>
                                </div>
                                <span style={{ fontSize: 10.5, color: tc.faint, letterSpacing: 0.1, whiteSpace: 'nowrap' }}>
                                    Brought to you by <a target="_blank" rel="noopener noreferrer" href="https://www.mirability.com" style={{ color: tc.muted, fontWeight: 600 }}>Mirability<sup>SM</sup></a>
                                </span>
                            </div>
                        </div>

                        )}

                        {/* Date nav: square outline arrows · serif period label · mono meta.
                            A slim strip in landscape-phone mode, where height is scarce. */}
                        <div className="relative flex items-center justify-center gap-3 sm:gap-4 px-3" style={{ minHeight: isCompactLandscape ? 40 : 54, borderBottom: `1px solid ${tc.gridLine}` }}>
                                    <button
                                        onClick={handleDecrement}
                                        className={`flex items-center justify-center ${theme.navArrowHover}`}
                                        style={{ width: isCompactLandscape ? 30 : 34, height: isCompactLandscape ? 30 : 34, borderRadius: 9, border: `1px solid ${tc.gridLine}`, background: tc.surface, color: tc.muted }}
                                        aria-label="Previous"
                                    >
                                        <i className="fas fa-chevron-left text-sm"></i>
                                    </button>

                                    {viewMode === 'week' ? (
                                        <button
                                            onClick={() => {
                                                setShowWeekPicker(!showWeekPicker);
                                                processedSlugRef.current = null;
                                            }}
                                            className={`${theme.navArrowHover} ${theme.text} px-2 py-1 rounded-lg whitespace-nowrap`}
                                            style={{ fontFamily: tokens.fonts.display, fontWeight: 500, fontSize: isCompactLandscape ? fs(19) : fsClamp(19, '4.5vw', 25) }}
                                        >
                                            {renderWeekRange()} <i className="fas fa-caret-down ml-1 text-sm align-middle" style={{ color: tc.muted }} aria-hidden="true"></i>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setShowMonthPicker(!showMonthPicker)}
                                            className={`${theme.navArrowHover} ${theme.text} px-2 py-1 rounded-lg whitespace-nowrap`}
                                            style={{ fontFamily: tokens.fonts.display, fontWeight: 500, fontSize: isCompactLandscape ? fs(19) : fsClamp(19, '4.5vw', 25) }}
                                        >
                                            {renderMonthName()} <i className="fas fa-caret-down ml-1 text-sm align-middle" style={{ color: tc.muted }} aria-hidden="true"></i>
                                        </button>
                                    )}

                                    <button
                                        onClick={handleIncrement}
                                        className={`flex items-center justify-center ${theme.navArrowHover}`}
                                        style={{ width: isCompactLandscape ? 30 : 34, height: isCompactLandscape ? 30 : 34, borderRadius: 9, border: `1px solid ${tc.gridLine}`, background: tc.surface, color: tc.muted }}
                                        aria-label="Next"
                                    >
                                        <i className="fas fa-chevron-right text-sm"></i>
                                    </button>

                                    <div className={isCompactLandscape ? 'block absolute right-4' : 'hidden lg:block absolute right-6'} style={{ fontFamily: tokens.fonts.mono, fontSize: fs(11), color: tc.faint, letterSpacing: 0.4 }}>
                                        {viewMode === 'week'
                                            ? `WEEK ${getWeekNumber(currentWeek)} · ${currentWeekEvents.length} EVENTS`
                                            : `${filteredEvents.length} EVENTS`}
                                    </div>

                                {/* Month Picker */}
                                {showMonthPicker && (
                                    <div className={`absolute z-50 mt-2 ${theme.modalBg} rounded-lg shadow-lg p-4 border ${theme.border} top-full left-1/2 w-72 -translate-x-1/2`}>
                                        <button
                                            onClick={gotoToday}
                                            className={`px-3 py-1 text-sm ${theme.actionButton} rounded-lg mb-2`}
                                        >
                                            Today
                                        </button>
                                        <div className="grid grid-cols-3 gap-2">
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <button
                                                    key={i+1}
                                                    onClick={() => {
                                                        setCurMonth(i+1);
                                                        setShowMonthPicker(false);
                                                        window.history.pushState({}, '', window.location.pathname);
                                                        setSelectedEventSlug(null);
                                                        processedSlugRef.current = null;
                                                    }}
                                                    className={`p-2 rounded-lg ${theme.text} ${
                                                        curMonth === i+1
                                                            ? theme.viewButtonActive
                                                            : new Date().getMonth() + 1 === i+1 && new Date().getFullYear() === curYear
                                                                ? theme.dayHeaderBg
                                                                : theme.pickerHover
                                                    }`}
                                                >
                                                    {new Date(0, i).toLocaleString('default', { month: 'short' })}
                                                </button>
                                            ))}
                                        </div>
                                        <div className={`flex items-center justify-center gap-2 mt-2 ${theme.text}`}>
                                            <button
                                                onClick={() => setCurYear(prevYear => prevYear - 1)}
                                                disabled={curYear === 2025}
                                                className={curYear === 2025 ? "opacity-50 cursor-not-allowed" : ""}
                                                aria-label={curYear === 2025 ? "Cannot go back before 2025" : "Previous year"}
                                            >
                                                <i className="fas fa-chevron-left"></i>
                                            </button>
                                            <span>{curYear}</span>
                                            <button
                                                onClick={() => setCurYear(prevYear => prevYear + 1)}
                                                aria-label="Next year"
                                            >
                                                <i className="fas fa-chevron-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Week Picker */}
                                {showWeekPicker && (
                                    <div className={`absolute z-50 mt-2 ${theme.modalBg} rounded-lg shadow-lg p-4 border ${theme.border} top-full left-1/2 transform -translate-x-1/2`}>
                                        <button
                                            onClick={gotoToday}
                                            className={`px-3 py-1 text-sm ${theme.actionButton} rounded-lg mb-2`}
                                        >
                                            Today
                                        </button>
                                        <div className="grid grid-cols-1 gap-1">
                                            {getWeeksInMonth(curYear, curMonth).map((weekStart, index) => {
                                                const weekEnd = new Date(weekStart);
                                                weekEnd.setDate(weekStart.getDate() + 6);

                                                const isCurrentWeek = weekStart.getTime() === currentWeek.getTime();
                                                const isCurrentMonth = weekStart.getMonth() === curMonth - 1;

                                                return (
                                                    <button
                                                        key={index}
                                                        onClick={() => {
                                                            setCurrentWeek(weekStart);
                                                            setShowWeekPicker(false);

                                                            const endOfSelectedWeek = new Date(weekStart);
                                                            endOfSelectedWeek.setDate(weekStart.getDate() + 6);
                                                            const selectedWeekMonth = weekStart.getMonth() + 1;
                                                            const selectedWeekYear = weekStart.getFullYear();
                                                            const endWeekMonth = endOfSelectedWeek.getMonth() + 1;
                                                            const endWeekYear = endOfSelectedWeek.getFullYear();

                                                            const daysInStartMonth = 7 - weekStart.getDay();
                                                            const daysInEndMonth = endOfSelectedWeek.getDay() + 1;

                                                            if (daysInStartMonth >= daysInEndMonth) {
                                                                if (selectedWeekMonth !== curMonth || selectedWeekYear !== curYear) {
                                                                    setValue(selectedWeekMonth);
                                                                    setYear(selectedWeekYear);
                                                                }
                                                            } else {
                                                                if (endWeekMonth !== curMonth || endWeekYear !== curYear) {
                                                                    setValue(endWeekMonth);
                                                                    setYear(endWeekYear);
                                                                }
                                                            }

                                                            window.history.pushState({}, '', window.location.pathname);
                                                            setSelectedEventSlug(null);
                                                            processedSlugRef.current = null;
                                                        }}
                                                        className={`p-2 rounded-lg text-left ${
                                                            isCurrentWeek
                                                                ? theme.viewButtonActive
                                                                : isCurrentMonth
                                                                    ? `${theme.text} ${theme.pickerHover}`
                                                                    : `${theme.secondaryText} ${theme.navArrowHover}`
                                                        }`}
                                                    >
                                                        {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                        </div>

                        {/* Organization Filter */}
                        {showFilters && (
                            <div className="rounded-lg m-3 p-4" style={{
                                background: tc.surface,
                                border: `1px solid ${tc.gridLine}`,
                                boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                                // Landscape phones: scroll within the panel rather than
                                // pushing the calendar entirely off the short screen.
                                ...(isCompactLandscape ? { maxHeight: 'calc(var(--app-height, 100vh) - 110px)', overflowY: 'auto' } : {}),
                            }}>
                                <OrganizationFilter
                                    organizations={organizations}
                                    selectedOrg={selectedOrg}
                                    onOrgChange={setSelectedOrg}
                                    theme={theme}
                                    tokens={theme.tokens}
                                    showStarredOnly={showStarredOnly}
                                    onStarredChange={setShowStarredOnly}
                                    hidePrivateEvents={hidePrivateEvents}
                                    onHidePrivateChange={handleHidePrivateChange}
                                    legendEvents={legendEvents}
                                    selectedCategory={selectedCategory}
                                    selectedTags={selectedTags}
                                    onCategoryClick={handleCategoryClick}
                                    onTagClick={handleTagClick}
                                />
                            </div>
                        )}
                    </div>

                    {/* Loading State */}
                    {isLoading && !initialLoadComplete && (
                        <div className={`fixed inset-0 ${theme.modalOverlay} flex items-center justify-center z-50`}>
                            <div className={`${theme.loadingBg} ${theme.loadingText} p-4 rounded-lg`}>
                                <i className="fas fa-spinner fa-spin fa-3x fa-fw"></i>
                                <span className="sr-only">Loading events...</span>
                            </div>
                        </div>
                    )}

                    {/* Screen reader live region — always in DOM so announcements are reliable */}
                    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                        {toast}
                    </div>

                    {/* Toast Message — visual only */}
                    {toast && (
                        <div aria-hidden="true" className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-yellow-200 text-yellow-900 px-6 py-3 rounded shadow-lg text-center font-semibold text-sm animate-fade-in-out">
                            {toast}
                        </div>
                    )}

                    {/* Dismissible disclaimer banner — shown on first visit only */}
                    {/* Landscape phones show this note in the rail's More menu instead. */}
                    {!disclaimerDismissed && !isCompactLandscape && (
                        <div className={`${theme.alertBackground} flex items-center justify-between px-4 py-1.5 text-sm`} role="alert">
                            <span>Note: Events are subject to change. Please verify with the event host.</span>
                            <button
                                onClick={dismissDisclaimer}
                                aria-label="Dismiss disclaimer"
                                className="ml-4 text-gray-600 hover:text-gray-900 shrink-0"
                            >
                                <i className="fas fa-times" aria-hidden="true"></i>
                            </button>
                        </div>
                    )}
                    <div className="flex-1 overflow-hidden min-h-0">

                        {viewMode === 'month' || viewMode === 'week' ? (
                            <div {...handlers} className="h-full">
                                                            <CalendarView
                                curMonth={curMonth}
                                curYear={curYear}
                                events={filteredEvents}
                                onModalStateChange={setIsModalOpen}
                                selectedEventSlug={selectedEventSlug}
                                onModalClose={handleModalClose}
                                isModalOpen={isModalOpen}
                                theme={theme}
                                onMonthChange={setCurMonth}
                                onYearChange={setCurYear}
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                currentWeek={currentWeek}
                                selectedEvent={selectedEvent}
                                onEventSelect={(event) => setSelectedEvent(event)}
                                onRightPaneChange={setIsRightPaneOpen}
                                isRightPaneOpen={isRightPaneOpen}
                                combinedEvents={combinedEvents}
                                clearSelectedEventSlug={clearSelectedEventSlug}
                                legendEvents={legendEvents}
                                selectedCategory={selectedCategory}
                                selectedTags={selectedTags}
                                onCategoryClick={handleCategoryClick}
                                onTagClick={handleTagClick}
                            />
                            </div>
                        ) : viewMode === 'list' ? (
                            <EventListView
                                events={filteredEvents}
                                curMonth={curMonth}
                                curYear={curYear}
                                theme={theme}
                                legendEvents={legendEvents}
                                selectedCategory={selectedCategory}
                                selectedTags={selectedTags}
                                onCategoryClick={handleCategoryClick}
                                onTagClick={handleTagClick}
                            />
                        ) : (
                            <EventMapView 
                                theme={theme}
                                events={filteredEvents}
                            />
                        )}
                    </div>
                </div>
            </div>

            {showLegal && (
                <footer className={`fixed ${theme.headerBg} ${theme.text} p-2 md:p-4 text-center bottom-0 left-0 right-0 z-10`} role="contentinfo">
                    <div className="container mx-auto">
                        
                        <nav className="mb-2 md:mb-4" role="navigation" aria-label="Footer navigation">
                            <div className="flex justify-center space-x-4 text-sm md:text-base">
                                <Link to="/privacy" className="hover:text-blue-700">Privacy</Link>
                                <Link to="/terms" className="hover:text-blue-700">Terms</Link>
                            </div>
                        </nav>
                        <div className="text-xs md:text-sm">
                            <p>ATLTech.events<sup>SM</sup> (atltech.events) and Event Horizon Media<sup>SM</sup> are the Service Marks of Mirability, LLC<sup>SM</sup>.</p>
                            <p>Copyright 2026 Mirability, LLC. All Rights Reserved.</p>
                        </div>
                    </div>
                </footer>
            )}

        </div>
    );
}

export default Home;
